import React from "react";
import { Castle, Star, Crown, Cake, UtensilsCrossed, Gift, Check } from "lucide-react";
import { useLang, t } from "../lib/i18n";

// Six chapters. Step index 1 is the current step (registration).
const STEPS = [
  { key: "step1", Icon: Castle,          hue: "#B57315" },
  { key: "step2", Icon: Star,            hue: "#E36FA6" },
  { key: "step3", Icon: Crown,           hue: "#C88B22" },
  { key: "step4", Icon: Cake,            hue: "#E4497D" },
  { key: "step5", Icon: UtensilsCrossed, hue: "#B57315" },
  { key: "step6", Icon: Gift,            hue: "#7D3EB0" },
];

const DEFAULT_CURRENT = 1;

export default function RoyalJourney({ currentIndex }) {
  const lang = useLang();
  const CURRENT_INDEX = typeof currentIndex === "number" ? currentIndex : DEFAULT_CURRENT;

  return (
    <section
      data-testid="royal-journey-roadmap"
      className="relative mb-4 mt-0"
      style={{ perspective: "1400px" }}
    >
      {/* Header — transparent, sits on page bg */}
      <div className="relative text-center mb-2">
        <h2
          data-testid="royal-journey-title"
          className="text-base sm:text-lg md:text-lg leading-tight inline-flex items-center gap-2"
          style={{
            fontFamily: "'Yatra One','Tiro Telugu','Noto Sans Telugu',serif",
            background: "linear-gradient(180deg,#B98A2F 0%,#8A5A1A 55%,#5C3D0F 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          <span className="text-[#8A6A1A]/70 text-xs">✦</span>
          {t("journey_title", lang)}
          <span className="text-[#8A6A1A]/70 text-xs">✦</span>
        </h2>
      </div>

      {/* The Road — SVG curved path with glowing dashes */}
      <div className="relative">
        {/* Desktop path */}
        <svg
          className="hidden md:block absolute left-0 right-0 top-[20px] w-full pointer-events-none"
          height="40"
          viewBox="0 0 1000 70"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="rjRoad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%"   stopColor="#D4AF37" stopOpacity="0.15" />
              <stop offset="20%"  stopColor="#D4AF37" stopOpacity="0.85" />
              <stop offset="80%"  stopColor="#D4AF37" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#D4AF37" stopOpacity="0.15" />
            </linearGradient>
            <filter id="rjGlow">
              <feGaussianBlur stdDeviation="2.4" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          {/* Curvy road: dips down between each stop */}
          <path
            d="M 20 35 Q 100 5, 180 35 T 340 35 T 500 35 T 660 35 T 820 35 T 980 35"
            stroke="url(#rjRoad)"
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
            strokeDasharray="6 10"
            filter="url(#rjGlow)"
          >
            <animate
              attributeName="stroke-dashoffset"
              from="0"
              to="-64"
              dur="6s"
              repeatCount="indefinite"
            />
          </path>
        </svg>

        {/* Mobile path (horizontal, thinner) */}
        <svg
          className="md:hidden absolute left-0 right-0 top-[16px] w-full pointer-events-none"
          height="30"
          viewBox="0 0 1000 30"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            d="M 20 15 Q 100 5, 180 15 T 340 15 T 500 15 T 660 15 T 820 15 T 980 15"
            stroke="url(#rjRoad)"
            strokeWidth="2"
            strokeLinecap="round"
            fill="none"
            strokeDasharray="4 8"
          />
        </svg>

        <ol className="relative grid grid-cols-6 gap-1 sm:gap-2">
          {STEPS.map((step, idx) => {
            const state =
              idx < CURRENT_INDEX ? "done" : idx === CURRENT_INDEX ? "current" : "upcoming";
            // Stagger the float animation so it feels alive
            const floatDelay = idx * 0.35;
            const tiltDir = idx % 2 === 0 ? 1 : -1;

            return (
              <li
                key={step.key}
                data-testid={`journey-step-${idx + 1}`}
                data-state={state}
                className="relative flex flex-col items-center gap-1 text-center"
                style={{ transformStyle: "preserve-3d" }}
              >
                <FloatingBadge
                  state={state}
                  Icon={step.Icon}
                  hue={step.hue}
                  index={idx + 1}
                  floatDelay={floatDelay}
                  tiltDir={tiltDir}
                />

                <div>
                  <h3
                    className={`text-[10px] sm:text-xs leading-tight ${
                      state === "upcoming" ? "text-ink/60" : "text-ink"
                    }`}
                    style={{
                      fontFamily: "'Yatra One','Tiro Telugu','Noto Sans Telugu',serif",
                    }}
                  >
                    {t(`journey_${step.key}_t`, lang)}
                  </h3>
                  {state === "current" && (
                    <span
                      className="inline-block mt-0.5 text-[0.5rem] tracking-[0.16em] uppercase px-1.5 py-[1px] rounded-full bg-[#E4497D]/15 text-[#B02D5C] border border-[#E4497D]/40"
                      style={{ fontFamily: "'Tiro Telugu',sans-serif" }}
                    >
                      ✦ {t("journey_now", lang)}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      {/* 3D + float keyframes */}
      <style>{`
        @keyframes rjFloat {
          0%, 100% { transform: translateY(0px) rotateY(-14deg) rotateX(6deg); }
          50%      { transform: translateY(-10px) rotateY(14deg) rotateX(-2deg); }
        }
        @keyframes rjFloatAlt {
          0%, 100% { transform: translateY(-6px) rotateY(12deg) rotateX(-4deg); }
          50%      { transform: translateY(4px)  rotateY(-12deg) rotateX(6deg); }
        }
        @keyframes rjFloatShadow {
          0%, 100% { transform: translateX(-50%) scale(1); opacity: 0.55; }
          50%      { transform: translateX(-50%) scale(0.75); opacity: 0.35; }
        }
        @keyframes rjPulseGlow {
          0%, 100% { box-shadow: 0 12px 30px -8px rgba(228,73,125,0.55),
                                 0 0 0 6px rgba(228,73,125,0.10),
                                 inset 0 -6px 12px rgba(0,0,0,0.25),
                                 inset 0 6px 10px rgba(255,255,255,0.35); }
          50%      { box-shadow: 0 18px 40px -6px rgba(228,73,125,0.75),
                                 0 0 0 10px rgba(228,73,125,0.14),
                                 inset 0 -6px 12px rgba(0,0,0,0.25),
                                 inset 0 6px 10px rgba(255,255,255,0.4); }
        }
      `}</style>
    </section>
  );
}

function FloatingBadge({ state, Icon, hue, index, floatDelay, tiltDir }) {
  const isDone = state === "done";
  const isCurrent = state === "current";

  // The badge itself is a 3D coin: two layered discs, an icon, and rim highlights
  const surface = isDone
    ? "linear-gradient(145deg, #F6D77A 0%, #D4AF37 45%, #8A6A1A 100%)"
    : isCurrent
    ? `linear-gradient(145deg, #FFD1E1 0%, ${hue} 55%, #7A1B47 100%)`
    : "linear-gradient(145deg, #FFFFFF 0%, #F1E7D0 55%, #C7B588 100%)";

  const iconColor = isDone ? "#3E2A0A" : isCurrent ? "#FFFFFF" : "#8A6A1A";
  const scale = isCurrent ? 1.08 : 1;
  const anim = tiltDir === 1 ? "rjFloat" : "rjFloatAlt";

  return (
    <div
      className="relative shrink-0"
      style={{
        width: "36px",
        height: "36px",
        transformStyle: "preserve-3d",
        transform: `scale(${scale})`,
      }}
    >
      {/* Shadow disc — creates 3D floor shadow */}
      <div
        className="absolute left-1/2 -translate-x-1/2"
        style={{
          bottom: "-6px",
          width: "30px",
          height: "6px",
          borderRadius: "50%",
          background: "radial-gradient(ellipse, rgba(74,22,53,0.35) 0%, rgba(74,22,53,0) 70%)",
          filter: "blur(2px)",
          animation: `rjFloatShadow 4.5s ease-in-out ${floatDelay}s infinite`,
        }}
        aria-hidden="true"
      />

      {/* Floating coin */}
      <div
        className="w-full h-full rounded-full flex items-center justify-center relative"
        style={{
          background: surface,
          transformStyle: "preserve-3d",
          animation: `${anim} 4.5s ease-in-out ${floatDelay}s infinite${
            isCurrent ? `, rjPulseGlow 2.4s ease-in-out infinite` : ""
          }`,
          boxShadow: isCurrent
            ? "0 12px 30px -8px rgba(228,73,125,0.55), inset 0 -6px 12px rgba(0,0,0,0.25), inset 0 6px 10px rgba(255,255,255,0.4)"
            : isDone
            ? "0 10px 24px -8px rgba(184,120,32,0.45), inset 0 -6px 12px rgba(0,0,0,0.18), inset 0 6px 10px rgba(255,255,255,0.55)"
            : "0 8px 20px -8px rgba(74,22,53,0.25), inset 0 -6px 12px rgba(0,0,0,0.12), inset 0 6px 10px rgba(255,255,255,0.6)",
        }}
      >
        {/* Inner rim highlight */}
        <div
          className="absolute inset-1 rounded-full pointer-events-none"
          style={{
            background:
              "conic-gradient(from 210deg, rgba(255,255,255,0.35), rgba(255,255,255,0) 40%, rgba(0,0,0,0.15) 70%, rgba(255,255,255,0.35))",
            mixBlendMode: "overlay",
            opacity: 0.7,
          }}
        />
        {/* Icon */}
        <span
          className="relative"
          style={{
            transform: "translateZ(18px)",
            color: iconColor,
            filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.35))",
          }}
        >
          {isDone ? (
            <Check size={16} strokeWidth={3.2} />
          ) : (
            <Icon size={16} strokeWidth={2.4} />
          )}
        </span>
      </div>

      {/* Number tag — floats slightly forward */}
      <span
        className="absolute -bottom-1 -right-0.5 min-w-[13px] h-[13px] px-0.5 rounded-full text-[8px] font-bold flex items-center justify-center border border-[#D4AF37]/60"
        style={{
          background: "linear-gradient(180deg, #FFF6E0 0%, #F1D98A 100%)",
          color: "#5C3D0F",
          boxShadow: "0 3px 6px rgba(74,22,53,0.25)",
          transform: "translateZ(24px)",
        }}
        aria-hidden="true"
      >
        {index}
      </span>
    </div>
  );
}
